import { SAH_READINESS_CHECK_CODES, type SahReadinessCheckCode } from './sah-delivery-ladder';

export type SahDeliveryCheckRow = {
  code: string;
  satisfied: boolean;
};

export type SahDeliveryCheckHrefContext = {
  clientId?: string | null;
  carePlanId?: string | null;
  /** When true, prefer client-scoped action surfaces when no exact plan action exists. */
  preferClientSurfaces?: boolean;
};

function clientHubFallback(clientId?: string | null): string {
  return clientId ? `/clients/${clientId}?tab=overview` : '/clients';
}

const COMPOSE_HASH_STAGE: Record<string, string> = {
  'composer-activate': 'activate',
  'composer-agree': 'agree',
  'composer-assess': 'understand',
  'composer-participation': 'understand',
  'import-inbox': 'understand',
  services: 'services-funding',
  budget: 'services-funding',
};

export function sahCarePlanStartPanelHref(clientId: string): string {
  return `/clients/${clientId}?tab=care-plans&panel=care-plan-start#care-plan-start`;
}

function composeHref(carePlanId: string | null | undefined, hash: string): string | null {
  if (!carePlanId) return null;
  const trimmed = hash.replace(/^#/, '');
  const stage = COMPOSE_HASH_STAGE[trimmed];
  const query = stage ? `?stage=${stage}` : '';
  return `/care-plans/${carePlanId}/compose${query}#${trimmed}`;
}

export function resolveSahDeliveryCheckHref(
  code: SahReadinessCheckCode | string,
  ctx: SahDeliveryCheckHrefContext,
): string {
  const { clientId, carePlanId, preferClientSurfaces = true } = ctx;
  const hub = clientId ? `/clients/${clientId}?tab=sah-hub` : null;
  const budget = clientId ? `/clients/${clientId}?tab=budget` : null;
  const detail = carePlanId ? `/care-plans/${carePlanId}` : null;
  const fallback = clientHubFallback(clientId);

  switch (code) {
    case 'ACTIVE_SUPPORT_AT_HOME_PLAN':
      return (
        composeHref(carePlanId, 'composer-activate') ??
        (clientId ? sahCarePlanStartPanelHref(clientId) : fallback)
      );
    case 'ACKNOWLEDGED_CARE_PLAN':
      return (
        composeHref(carePlanId, 'composer-agree') ??
        (clientId ? sahCarePlanStartPanelHref(clientId) : fallback)
      );
    case 'DATE_VALID_SERVICE_INCLUSIONS':
      return (
        composeHref(carePlanId, 'services') ??
        (clientId ? sahCarePlanStartPanelHref(clientId) : fallback)
      );
    case 'SIGNED_SERVICE_AGREEMENT':
      if (preferClientSurfaces && hub) return `${hub}&action=create-agreement`;
      return (
        composeHref(carePlanId, 'composer-agree') ??
        (hub ? `${hub}&action=create-agreement` : fallback)
      );
    case 'GENERAL_CARE_CONSENT':
      if (preferClientSurfaces && clientId) return `/clients/${clientId}?tab=consent`;
      return detail ?? hub ?? fallback;
    case 'CURRENT_QUARTER_BUDGET':
      if (preferClientSurfaces && budget) return `${budget}&panel=funding#funding`;
      return composeHref(carePlanId, 'budget') ?? budget ?? fallback;
    case 'HCP_OPENING_RECORDED':
      if (preferClientSurfaces && budget) return `${budget}&panel=hcp-opening#hcp-opening`;
      return budget
        ? `${budget}&panel=hcp-opening#hcp-opening`
        : (composeHref(carePlanId, 'budget') ?? fallback);
    case 'SAH_MEANS_BASIS':
      if (preferClientSurfaces && budget) return `${budget}&panel=funding-setup#funding-setup`;
      return composeHref(carePlanId, 'budget') ?? budget ?? fallback;
    default:
      return hub ?? detail ?? fallback;
  }
}

export type SahDeliveryCheckQueueItem = {
  id: string;
  title: string;
  detail: string;
  href: string;
  severity: 'blocker' | 'review' | 'watch';
  owner: string;
  code: SahReadinessCheckCode;
};

export function buildSahDeliveryCheckQueueItems(
  checks: readonly SahDeliveryCheckRow[],
  ctx: SahDeliveryCheckHrefContext & { owner?: string },
): SahDeliveryCheckQueueItem[] {
  const byCode = new Map(checks.map((row) => [row.code, row]));
  const owner = ctx.owner ?? 'Coordinator';

  return SAH_READINESS_CHECK_CODES.flatMap((metadata) => {
    const check = byCode.get(metadata.code);
    if (!check || check.satisfied || metadata.advisory) return [];
    return [
      {
        id: `delivery-check-${metadata.code}`,
        title: metadata.label,
        detail: metadata.detail ?? 'Capture the required delivery-readiness evidence.',
        href: resolveSahDeliveryCheckHref(metadata.code, ctx),
        severity: 'blocker' as const,
        owner,
        code: metadata.code,
      },
    ];
  });
}

export function hasUnsatisfiedDeliveryCheck(
  checks: readonly SahDeliveryCheckRow[],
  code: SahReadinessCheckCode,
): boolean {
  const row = checks.find((entry) => entry.code === code);
  return Boolean(row && !row.satisfied);
}
